import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #38 - A faux AI that writes JavaScript",
  "description": "",
  "published": "2017-05-22T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-38-faux-ai-writes-javascript/swizec/7607"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should follow `}<a parentName="em" {...{
          "href": "https://www.youtube.com/channel/UCoyHgaeLLI7Knp7LDHOwZMw"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "N_k1c0sed_w",
      "videostartat": "0"
    }}></lite-youtube>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Really what we need is AI. We write the tests, AI builds code that passes.`}<br parentName="p"></br><br parentName="p"></br>{`True test driven development!`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/865295043162021889"
      }}>{`May 18, 2017`}</a></blockquote>
    <p>{`Think about how a product manager writes code.`}</p>
    <p>{`They define some features, come up with constraints, and unleash a team of engineers. A few days pass while stuff they don't understand goes on, and voilà! New features with working code and business metrics twitching to and fro as users respond to new goodies.`}</p>
    <p>{`Great, huh?`}</p>
    <p>{`Now think about how a software engineer writes code.`}</p>
    <p>{`They talk to the product manager and get a `}<em parentName="p">{`"This is what it should do"`}</em>{` spec, then they agree on `}<em parentName="p">{`"This is how much it should cost in time/resources to build"`}</em>{`. Then the engineer looks at existing code and gets a `}<em parentName="p">{`"This is the code the new stuff should fit into"`}</em>{`. Then they come up with a set of tests, automated or otherwise, that answer the question, `}<em parentName="p">{`"Does it work yet?"`}</em></p>
    <p>{`With tests in hand, the engineer sets to work. Painstakingly manipulating characters on screen, typing and typing and running and testing and eventually they have code that is syntactically correct and doesn't break existing stuff and implements the new stuff. Hooray!`}</p>
    <p>{`Great, huh?… kinda tedious 🤔`}</p>
    <p>{`Your core value add as an engineer is to help the product manager come up with sensible constraints. To help them include the new stuff into the existing stuff. To suggest constraints they didn't think of. And to add constraints they shouldn't have to worry about.`}</p>
    <p>{`All that code writing stuff… meh.`}</p>
    <p>{`I know, it's not just typing. It takes smarts and experience, and it's really hard to do well. But if you had good tests and infinite memory and infinite time, you could keep generating random strings until one of them happened to make the tests pass. You'd call that string The Code.`}</p>
    <p>{`Similar idea as the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Monkey_typewriter_theory"
      }}>{`monkeys with typewriters`}</a>{` thought experiment and the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Bogosort"
      }}>{`random sort algorithm`}</a>{`. Do random stuff until requirements are met.`}</p>
    <p>{`Expected running time: Infinity.`}</p>
    <p>{`Because you're smart and experienced and a grizzled veteran or an eager newbie, it takes you less than infinity to write that code. You use smart heuristics like `}<em parentName="p">{`"not typing random stuff"`}</em>{` and `}<em parentName="p">{`"actually thinking about what to do"`}</em>{`.`}</p>
    <p>{`Although I often just keep trying random things when things get really tough. 😇`}</p>
    <p>{`Wouldn't it be cool if someone could do that for you? I mean, if `}<a parentName="p" {...{
        "href": "https://www.wired.com/2015/10/this-news-writing-bot-is-now-free-for-everyone/"
      }}>{`computers can write news`}</a>{`, why not the code that makes computers write news?`}</p>
    <p>{`Here's the idea: You write the tests, AI makes them pass.`}</p>
    <h2 {...{
      "id": "experiment-1---failed"
    }}>{`Experiment 1 - failed`}</h2>
    <p>{`I'm calling this first experiment “faux AI” because it doesn't know what it's doing. The current approach is at the `}<em parentName="p">{`"Paste random characters and fingers crossed"`}</em>{` stage.`}</p>
    <p>{`We tried an `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Evolutionary_algorithm"
      }}>{`evolutionary algorithm`}</a>{` based on manipulating code as character strings. This did not work.`}</p>
    <p>{`Our population kept getting stuck at a maximum far away from the result. Once every member of the population had the same fitness score, they stopped changing and a solution was never reached.`}</p>
    <p>{`The situation improved once we tweaked our fitness function and added constraint weights. Instead of having a series of `}<inlineCode parentName="p">{`true`}</inlineCode>{`/`}<inlineCode parentName="p">{`false`}</inlineCode>{` conditions, we weighted them by importance.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// We aim to maximize this value
function fitness(code, conditions) {
  const max = conditions.reduce(
      (sum, { condition, weight }) => sum + weight,
      0
    ),
    fit = conditions
      .map(({ condition, weight }) => Number(condition(code)) * weight)
      .reduce((sum, fit) => sum + fit, 0);

  return fit / max;
}

const Conditions = [
  {
    condition: (code) => !(run(code) instanceof Error),
    weight: 10,
  },
  {
    condition: (code) => run(code) === 4,
    weight: 8,
  },
  {
    condition: (code) => code !== "4",
    weight: 2,
  },
  {
    condition: (code) => code !== 4,
    weight: 2,
  },
  {
    condition: (code) => (code.length > 4 ? 4 / code.length : 1),
    weight: 6,
  },
];
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`fitness`}</inlineCode>{` function takes a piece of code and walks through a set of `}<inlineCode parentName="p">{`conditions`}</inlineCode>{`. We test each condition and multiply the result with its weight. A syntactically valid piece of code gets `}<inlineCode parentName="p">{`10`}</inlineCode>{`, an invalid one gets `}<inlineCode parentName="p">{`0`}</inlineCode>{`. One that produces the correct result gets`}<inlineCode parentName="p">{`8`}</inlineCode>{`, an invalid result gets `}<inlineCode parentName="p">{`0`}</inlineCode>{`, etc.`}</p>
    <p>{`We're looking for code that is shorter than `}<inlineCode parentName="p">{`4`}</inlineCode>{` characters, isn't `}<inlineCode parentName="p">{`4`}</inlineCode>{` or `}<inlineCode parentName="p">{`"4"`}</inlineCode>{`, and returns `}<inlineCode parentName="p">{`4`}</inlineCode>{`. A good solution would be, say, `}<inlineCode parentName="p">{`2+2`}</inlineCode>{`, or `}<inlineCode parentName="p">{`1+3`}</inlineCode>{`. Even `}<inlineCode parentName="p">{`a = 4`}</inlineCode>{` works. Or maybe `}<inlineCode parentName="p">{`"afwaelfjlkwefj"; 4`}</inlineCode>{`.`}</p>
    <p>{`The tricky part of evolutionary algorithms is that even when they work, their results are weird. Best things in the world at finding loopholes in your conditions.`}</p>
    <p>{`Our evolutionary algorithm looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function* generation({ population, fitness, N }) {
  while (true) {
    population = rank(population, fitness);

    // pairwise breed top 50% of population
    population = population.concat(
      breedPairs(_.chunk(_.take(population, population.length / 2), 2))
    );

    population = rank(population, fitness);

    population = _.take(population, BIGGEST_POPULATION);

    yield {
      fitness: fitness(population[0], Conditions),
      code: population[0],
      fitnessLast: fitness(population[population.length - 1], Conditions),
      codeLast: population[population.length - 1],
      size: population.length,
    };
  }
}
`}</code></pre>
    <p>{`That's a generator, by the way. Call `}<inlineCode parentName="p">{`.next()`}</inlineCode>{` to get the next generation from the current population. Call it as many times as you want; it keeps working :)`}</p>
    <p>{`Each `}<inlineCode parentName="p">{`generation`}</inlineCode>{` starts with an existing `}<inlineCode parentName="p">{`population`}</inlineCode>{`, which is ranked by the `}<inlineCode parentName="p">{`fitness`}</inlineCode>{` function. We make pairs out of the best 50% of the specimens, breed them, and add their offspring to the population.`}</p>
    <p>{`The new population is again ranked, and if there's more specimen than we have room for, the bottom few are thrown away. They're useless.`}</p>
    <p>{`You can see how the `}<inlineCode parentName="p">{`breedPairs`}</inlineCode>{` and `}<inlineCode parentName="p">{`rank`}</inlineCode>{` functions work `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/faux-js-ai"
      }}>{`on Github`}</a>{`.`}</p>
    <p>{`This current approach did not produce a single working piece of code. This was the best solution with a score of `}<inlineCode parentName="p">{`0.5535714285714286`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`" pci  s |ePke "
`}</code></pre>
    <p>{`At least it's syntactically valid 🤔`}</p>
    <p>{`This field of research is known as `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Genetic_programming"
      }}>{`genetic programming`}</a>{`. I don't think anyone has gotten it to the point of replacing software engineers yet, but I remember a cool project from my high school days by some Slovenian person called `}<a parentName="p" {...{
        "href": "http://www.critticall.com/"
      }}>{`Critticall`}</a>{`. It was able to invent novel sorting algorithms.`}</p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`The experiment failed, and the code is slow.`}</p>
    <p>{`Maybe we should switch to Python? Or parallelize the generation generator. Evolution is, after all, highly parallelizable and there `}<em parentName="p">{`are`}</em>{` 4 cores in my computer…`}</p>
    <p>{`Fundamentally, we're gonna have to change the level of abstraction. You cannot evolve code as if it was a random series of characters. Maybe if our algorithm could understand syntax, or at least JavaScript symbols, and evolve an `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Abstract_syntax_tree"
      }}>{`AST`}</a>{`, then it might work.`}</p>
    <p>{`I think there's also room for neural nets and deep learning in the evolution step. 🤔`}</p>
    <p>{`Ideas very welcome. I will continue to dabble with this as time allows.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      